<template lang="pug" src="./operators.pug"></template>
<style lang="scss" src="./operators.scss" scoped> </style>

<script>
import { mapState, mapGetters } from 'vuex';
import { copyToClipboard } from '@/utils/helpers';

import { validationMixin } from 'vuelidate';
import validate from '@/mixins/validate';
import { requiredIf, minLength } from 'vuelidate/lib/validators';

import config from '@/config.json';

export default {
    mixins: [validate, validationMixin],
    data: () => ({
        operatorStatuses: config.operatorStatuses,
        startDateModal: false,
        endDateModal: false,
        requestProcessing: false,
        company: {
            title: null
        },
        companyEditDialog: false,
    }),
    validations: {
        company: {
            title: {
                required: requiredIf(function() {
                    return this.companyEditDialog;
                }),
                minLength: minLength(3)
            }
        }
    },
    computed: {
        ...mapGetters(['roleName', 'userBrandId', 'user']),
        ...mapState({
            items: state => state.users.operators.items,
            pagination: state => state.users.operators.pagination
        }),
        headers() {
            const properties = [ 'userId', 'fullName', 'phoneNumber', 'email', 'loginName', 'comment', 'roleName', 'createdAt', 'lastActiveTime'];

            if (['removed'].includes(this.filterBy)) {
                properties.push('updatedAt');
            }

            if (!this.userBrandId) {
                properties.push('brandId');
            }

            properties.push('establishmentId');

            return [...properties, 'actions'].map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.Users.table.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        },
        rolesSelect() {
            const roles = [
                'all', 'intern', 'operator', 'accountant',
                'manager', 'corporate-admin', 'corporate-user',
                'boss', 'client', 'sales-manager', 'leader',
                'admin-delivery',
                'admin-establishment',
                'manager-establishment'
            ];

            if (this.user.roleName == 'admin') {
                roles.push('admin');
            }

            return roles;
        },
        canShowRolesFilter() {
            if (!this.user || !this.user.roleName) {
                return false;
            }
            return ['boss', 'manager', 'admin'].includes(this.user.roleName);
        },
        query: {
            get() {
                return this.$store.state.users.operators.filters.query;
            },
            set(val) {
                return this.$store.commit('users/SET_OPERATORS_QUERY', val);
            }
        },
        filterBy: {
            get() {
                return this.$store.state.users.operators.filters.filterBy;
            },
            set(val) {
                return this.$store.commit('users/SET_OPERATORS_FILTER_BY', val);
            }
        },
        chosenRoleName: {
            get() {
                return this.$store.state.users.operators.filters.roleName;
            },
            set(val) {
                return this.$store.commit('users/SET_OPERATORS_ROLE_NAME', val);
            }
        },
        startDate: {
            get() {
                return this.$store.state.users.operators.filters.startDate;
            },
            set(val) {
                return this.$store.commit('users/SET_OPERATORS_START_DATE', val);
            }
        },
        endDate: {
            get() {
                return this.$store.state.users.operators.filters.endDate;
            },
            set(val) {
                return this.$store.commit('users/SET_OPERATORS_END_DATE', val);
            }
        }
    },
    created() {
        this.filterBy = config.operatorStatuses[0];

        this.$store.dispatch('data/getBrands');
        this.loadItems();
    },
    beforeDestroy() {
        this.$store.commit('users/RESET_OPERATORS_FILTERS');
    },
    methods: {
        copyToClipboard,
        loadItems(page = 1) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            this.$store.dispatch('users/getOperators', Object.assign({}, {
                q: this.query,
                filterBy: this.filterBy,
                startDate: this.startDate,
                roleName: this.chosenRoleName,
                endDate: this.endDate,
                page
            })).then(() => {
                this.$nextTick(() => this.requestProcessing = false);
            });
        },
        onPageChange(page) {
            if (page !== this.pagination.page) {
                this.loadItems(page);
            }
        },
        changeEndDate() {
            this.endDateModal = false;
            this.loadItems();
        },
        changeStartDate() {
            this.startDateModal = false;
            this.loadItems();
        },
        onRoleChange(roleName) {
            this.chosenRoleName = roleName === 'all' ? null : roleName;
            this.loadItems();
        },
        canEditRole: function(role) {
            // TODO: Refactor this shit
            return !(
                (['client', 'driver', 'operator', 'accountant', 'manager', 'sales-manager', 'intern', 'corporate-user'].includes(this.roleName) && ['intern', 'operator', 'operatorExtended', 'accountant', 'manager', 'sales-manager'].includes(role)) ||
                (['admin'].includes(this.roleName) && ['intern', 'operator', 'operatorExtended', 'accountant', 'manager', 'boss', 'sales-manager', 'corporate-admin', 'admin', 'leader', 'admin-delivery', 'admin-establishment', 'manager-establishment'].includes(role)) ||
                (['boss'].includes(this.roleName) && ['intern', 'operator', 'operatorExtended', 'accountant', 'manager', 'boss', 'sales-manager'].includes(role)) ||
                (['corporate-admin'].includes(this.roleName) && ['corporate-user'].includes(role)) ||
                (['admin-delivery'].includes(this.roleName) && ['admin-delivery', 'admin-establishment', 'manager-establishment'].includes(role)) ||
                (['admin-establishment'].includes(this.roleName) && ['admin-establishment', 'manager-establishment'].includes(role))
            );
        },
        checkRoleName: function(item) {
            const arr = ['corporate-admin', 'corporate-user'];
            return this.user.roleName === 'admin' && arr.includes(item.roleName);
        },
        openCompanyEditDialog: function(company, companyId) {
            this.companyEditDialog = true;

            this.company = Object.assign({}, {
                id: companyId,
                ...Object.select(company, Object.keys(this.company))
            });
        },
        saveCompany: function() {
            if (this.$v.company.$invalid) {
                this.$v.company.$touch();
                return;
            }

            this.$store.dispatch('users/updateCompany', this.company).then(res => {
                const { id: companyId } = res.data;

                const editedOperator = this.items.find(it => it.companyId == companyId);

                if (editedOperator) {
                    editedOperator.company = Object.assign({}, {
                        ...Object.reject(res.data, ['id'])
                    });
                }

                this.$nextTick(() => {
                    this.companyEditDialog = false;
                    this.$v.company.$reset();
                });
            });
        },
    }
};
</script>
